import { useTranslation } from 'react-i18next';
import { Alert } from '@/shared/ui';
import { useAgendaParams } from '@/shared/navigation/react/useAgendaParams';
import { useWindowTitle } from '@/shared/windowing';
import { DETAIL_QUERY_STALE_TIME_MS } from '@/shared/lib/queryClient';
import { useGetProductsStation } from '@/api/generated/products-stations/products-stations';
import { stationDetailAgenda } from './stationDetailAgenda';
import { StationDetailView } from './StationDetailView';
import { emptyStationFormValues, fromGetStationResponse } from './stationFormSchema';

interface StationDetailPageProps {
  layerId: string;
}

/** Thin loader: vyřeší parametr agendy a (v editaci) načtení detailu stanice. */
export function StationDetailPage({ layerId }: StationDetailPageProps) {
  const { t } = useTranslation('products');
  const { open } = useAgendaParams(stationDetailAgenda, layerId);
  const stationId = open.stationId;
  const isEdit = stationId != null;

  // Krátký staleTime (jednotky sekund) — detail se při každém otevření načte znovu, cache jen pro
  // rychlé překlikávání. Po uložení navíc detail invalidujeme (viz StationDetailView.onSaved).
  const stationQuery = useGetProductsStation(stationId ?? 0, {
    query: { enabled: isEdit, staleTime: DETAIL_QUERY_STALE_TIME_MS },
  });

  // Dynamický název okna (taskbar / historie / hlavička) = název stanice (v editaci).
  useWindowTitle(stationQuery.data?.name);

  if (isEdit && stationQuery.isLoading) {
    return <p className="text-phx-text-muted text-phx-sm p-phx-5">{t('station.loading')}</p>;
  }
  if (isEdit && stationQuery.isError) {
    return (
      <div className="p-phx-5">
        <Alert severity="error" title={t('station.loadErrorTitle')}>
          {t('station.loadErrorDescription')}
        </Alert>
      </div>
    );
  }

  return (
    <StationDetailView
      layerId={layerId}
      stationId={stationId}
      initialValues={
        stationQuery.data ? fromGetStationResponse(stationQuery.data) : emptyStationFormValues()
      }
    />
  );
}
